import { Component, OnInit, Input, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ProfileService,
  DistributorsService,
  StoreService,
  ItinerariesService,
  JobsService,
  CoursesService
} from 'core/services';
import { Profile } from 'core/models/agent';
import { TranslateService } from '@ngx-translate/core';
import { Client } from 'core/models/client';
import { PlansService } from 'core/services/plans.service';
import { BUTTON_ACTIONS_ITINERARIES, OWNER_ITINERARY_OPTIONS, SCOPES } from '../assets/constants';
import { Course } from 'core/models/course';
import { Plan } from 'core/models/plan';
import { Subscription } from 'rxjs';
import { ChangesAccessComponent, SendDiplomaComponent } from './bulk-actions-itineraries';

@Component({
  selector: 'lms-itineraries',
  templateUrl: './itineraries.component.html',
  styleUrls: ['./itineraries.component.scss']
})
export class ItinerariesComponent implements OnInit {
  @ViewChild('changeAccess', { static: false })
  public changeAccess: ChangesAccessComponent;
  @ViewChild('sendDiploma', { static: false })
  public sendDiploma: SendDiplomaComponent;
  @Input() listId: any = 'ITINERARIES_LIST';
  @Input() client: Client;
  // to determine component scope: 'itineraries' (route) or 'client'
  @Input() scope: string = SCOPES.itineraries;
  public catalogueId: string;
  public user: Profile;
  public itineraries: Course[] = [];
  public options: any = {
    filters: {},
    selectedColumns: ['title', 'clientName', 'duration', 'courses', 'accessMode', 'shouldSendDiploma', 'status'],
    filterList: ['title', 'itineraryDuration', 'courses', 'accessMode', 'shouldSendDiploma', 'clientName', 'status']
  };
  public isCorporateWithSubscriptionClient: boolean = true;
  public previewCampusProfileAction: string = 'courses.previewCampus';
  public ownerOptions: any[];
  public selectedOwner: string = 'all';
  private ownerConditions: any = {};
  private dataSource: any;
  public dataSubscription: Subscription;
  public defaultSendDiploma: boolean;

  constructor(
    public router: Router,
    public activatedRoute: ActivatedRoute,
    public profileService: ProfileService,
    public distributorsService: DistributorsService,
    public itinerariesService: ItinerariesService,
    public coursesService: CoursesService,
    public plansService: PlansService,
    public translate: TranslateService,
    public elementRef: ElementRef,
    public jobsService: JobsService
  ) {
    this.profileService.getLoggedUser$().subscribe((user) => {
      this.user = user;
    });
  }

  ngOnInit() {
    this.activatedRoute.data.subscribe((data) => {
      this.setCatalog(data['courses']);
      this.client = this.scope === SCOPES.itineraries ? data['client'] : this.client;
      if (this.scope === SCOPES.itineraries && !this.client) {
        this.options.selectedColumns = ['title', 'clientName', 'duration', 'status', 'courses'];
        this.options.filterList = ['title', 'clientName', 'courses', 'itineraryDuration', 'status'];
      }
      this.isCorporateWithSubscriptionClient = this.client?.subscriptions?.users;
      this.previewCampusProfileAction =
        this.scope === SCOPES.itineraries ? 'courses.previewCampus' : 'clients.previewCampus';

      const clientId = this.client ? this.client.id : this.user.clientId;
      const isExpert = this.user.role === 'expert';
      const isDistributor = this.user.role === 'distributor';
      const partnerId = (this.client && this.client.distributorId) || (isDistributor && this.user.distributorId);

      this.ownerConditions = {
        clientId,
        isExpert,
        isDistributor,
        client: this.client,
        editorClient: this.client && this.client.plan && this.client.plan.editorAccess,
        partner: null,
        editorPartner: false,
        onClientDetail: this.scope === SCOPES.clients
      };
      this.setOwnerOptions();
      if (partnerId) {
        this.distributorsService.getDistributor(partnerId).subscribe((partner) => {
          this.ownerConditions.partner = partner;
          if (partner.type === 'expert') {
            this.ownerConditions.isExpert = true;
            this.ownerConditions.editorPartner = true;
            this.setOwnerOptions();
          } else {
            this.plansService.getClientPlan(partner.clientId).then((plan: Plan) => {
              if (plan && plan.editorAccess) {
                this.ownerConditions.editorPartner = true;
              }
              this.setOwnerOptions();
            });
          }
        });
      }

      this.options.filters.selectedOwner = this.selectedOwner;
      this.options.filters.clientId = clientId;
      this.options.filters.partnerId = partnerId;
    });

    this.dataSubscription = StoreService.get(this.listId).subscribe((dataSource) => (this.dataSource = dataSource));
  }

  public ngOnDestroy(): void {
    this.dataSubscription.unsubscribe();
  }

  setCatalog(catalog: any = {}) {
    const { itineraries = [], defaultAccessMode } = catalog;
    this.itineraries = itineraries
      .filter((itinerary) => !itinerary.archived)
      .map((itinerary) => {
        return { ...this.coursesService.getCachedCourseData(itinerary.id), title: itinerary.title || itinerary.name };
      });
    this.options.defaultAccessMode = defaultAccessMode;
    this.catalogueId = catalog.id;
    this.defaultSendDiploma = catalog.configuration?.shouldSendDiploma;
  }

  setOwnerOptions() {
    this.ownerOptions = OWNER_ITINERARY_OPTIONS.filter((option) => option.isActiveOption(this.ownerConditions)).map(
      (option) => ({
        key: option.key,
        value: this.translate.instant(option.value)
      })
    );
  }

  goToCreateItinerary(): void {
    this.router.navigate(['itineraries', 'new']);
  }

  async onChangeSelectedOwner(selectedOwner: string) {
    this.selectedOwner = selectedOwner;
    const filters = { ...this.dataSource.filters };
    filters.selectedOwner = selectedOwner;
    await this.dataSource.refreshData({ filters, page: 1 });
    StoreService.update(this.listId, this.dataSource);
  }

  onCourseEvent(event: any) {
    this.updateItineraries();
  }

  private updateItineraries(): void {
    // TODO itinerary try to update cached catalog
    // if (this.ownerConditions.clientId) {
    //   resolve(this.coursesService.loadClientCatalogueCache(clientId, true));
    // } else {
    //   resolve(this.coursesService.loadAccountCoursesCache(true));
    // }
    this.itinerariesService.getAccountItineraries().then((data: any[]) => {
      this.itineraries = data.filter((itinerary) => !itinerary.archived);
    });
  }

  itineraryBulkActions(itinerary) {
    const defaultAccessMode = this.options.defaultAccessMode;
    switch (itinerary.action) {
      case 'changeAccess':
        this.changeAccess.openModal(itinerary.selectedCourses, defaultAccessMode);
        break;
      case 'shouldSendDiploma':
        this.sendDiploma.openModal(itinerary.selectedCourses, this.defaultSendDiploma);
        break;
    }
  }

  onConfirmBulkAction(jobData) {
    jobData.clientId = this.client ? this.client.id : null;
    this.itinerariesService.updateItinerariesBulk(jobData).then((job: any) => {
      const jobSubscription = this.jobsService.manageJob(job.jobId, jobData.jobType).subscribe((jobStatus) => {
        if (jobStatus.status === 'finished' || jobStatus.status === 'fatal-error') {
          jobSubscription.unsubscribe();
          this.coursesService.loadClientCatalogueCache(this.client.id, true, true).then((catalog) => {
            this.setCatalog(catalog);
          });
        }
      });
    });
  }

  get bulkActions() {
    if (this.selectable && this.client) {
      return BUTTON_ACTIONS_ITINERARIES;
    }
    return null;
  }

  get selectable() {
    return this.profileService.canExecute('courses.bulk') && (this.scope === SCOPES.clients || !!this.client);
  }
}
